import React, { useState, useMemo } from 'react';
import { useTranslation } from '../../utils/LanguageContext';
import { ClockIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Hash, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import HighlightedText from './HighlightedText';

const ModuleCard = ({ module, lessons = [], searchPhrases = [] }) => {
  const { t } = useTranslation();
  const [showLessons, setShowLessons] = useState(false);
  
  const {
    name,
    name_code,
    name_normed,
    learning_objectives,
    estimated_hours,
    complexity_level
  } = module;

  const moduleLessons = useMemo(() => 
    lessons.filter(lesson => 
      lesson.name_normed_modules?.includes(name_normed)
    ),
    [lessons, name_normed]
  );

  // Map the complexity level to lowercase English key for consistent color mapping
  const complexityMap = {
    'a': 'a',
    'b': 'b',
    'c': 'c',
    'd': 'd'
  };
  
  const complexityKey = complexityMap[complexity_level] || complexity_level.toLowerCase();
  
  const complexityColor = {
    a: 'bg-green-100 text-green-800',
    b: 'bg-blue-100 text-blue-800',
    c: 'bg-purple-100 text-purple-800',
    d: 'bg-red-100 text-red-800'
  }[complexityKey] || 'bg-gray-100 text-gray-800';

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">
              <HighlightedText text={name} highlights={searchPhrases} />
            </h3>
            <div className="flex items-center text-sm text-gray-500 mb-2">
              <Hash className="h-4 w-4 mr-1" />
              <span>{name_code}</span>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${complexityColor}`}>
            {t(`modulesPage.complexity.${complexityKey}`)}
          </span>
        </div>
       
        <div className="mt-4 text-gray-600 leading-relaxed">
          <HighlightedText text={learning_objectives} highlights={searchPhrases} />
        </div>
        
        <div className="mt-4 flex items-center text-gray-500">
          <ClockIcon className="h-5 w-5 mr-2" />
          <span>{estimated_hours} {t('modulesPage.card.hours')}</span>
        </div>

        {moduleLessons.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <button
              onClick={() => setShowLessons(!showLessons)}
              className="w-full flex items-center justify-between text-blue-600 hover:text-blue-700 transition-colors"
            >
              <div className="flex items-center">
                <BookOpen className="h-5 w-5 mr-2" />
                <span className="font-medium">
                  {t('modulesPage.card.includedLessons', 'Included Lessons')}{':'} {moduleLessons.length}
                </span>
              </div>
              {showLessons ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>

            {showLessons && (
              <div className="mt-3 space-y-3">
                {moduleLessons.map((lesson) => (
                  <div
                    key={lesson.name_code}
                    className="bg-gray-50 rounded-md p-3"
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          <HighlightedText text={lesson.name} highlights={searchPhrases} />
                        </h4>
                        <p className="mt-1 text-sm text-gray-500">
                          <HighlightedText text={lesson.learning_objectives} highlights={searchPhrases} />
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleCard;